/** Shared bits for the attendance screen. The server works out hours and pay; this only formats. */

export const OUT_REASONS: { key: string; label: string }[] = [
  { key: "BREAK", label: "Break" },
  { key: "LUNCH", label: "Lunch" },
  { key: "HOME", label: "Home" },
  { key: "ERRAND", label: "Errand" },
  { key: "OTHER", label: "Other" },
];

export const reasonLabel = (key?: string | null): string =>
  key === "SWITCH" ? "Switched work" : OUT_REASONS.find((r) => r.key === key)?.label || key || "";

export type Activity = { id: string; code: string; name: string };

/** Loom work, or time work on an activity. */
export type Work = { payBasis: "PIECE" | "TIME"; activityId?: string | null };

export const workLabel = (payBasis?: string | null, activity?: { name?: string } | null): string =>
  payBasis === "PIECE" ? "Loom work" : activity?.name || "Time work";

/** 460 → "7 h 40 m"; 45 → "45 m". */
export const hours = (minutes?: number | null): string => {
  const m = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  // "min", not "m": on the loom page "m" means metres.
  if (!h) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
};

export const nowHHMM = (d: Date = new Date()): string =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

export type PersonState = "IN" | "OUT" | "FIX";

/** In now, out (gone home or on a break), or needs fixing (a missing In or Out). */
export const stateOf = (p: any): PersonState => {
  const flags: string[] = p?.flags || [];
  if (flags.includes("UNPAIRED_IN") || flags.includes("UNPAIRED_OUT")) return "FIX";
  return p?.stillIn ? "IN" : "OUT";
};

/** The last Out's reason, e.g. "Lunch", for someone who is out. */
export const lastOutReason = (p: any): string => {
  const outs = (p?.punches || []).filter((x: any) => x.type === "OUT");
  return outs.length ? reasonLabel(outs[outs.length - 1].reason) : "";
};

export const fixMessage = (flags: string[] = []): string => {
  if (flags.includes("UNPAIRED_IN")) return "An In has no Out. Add the Out (or undo the extra In) so the day can be paid.";
  if (flags.includes("UNPAIRED_OUT")) return "An Out has no In before it. Add the In so the day can be paid.";
  return "";
};

/** Time pay lines from the server come per stretch; show one line per activity. */
export const linesByActivity = (lines: any[] = []): any[] => {
  const out: any[] = [];
  const at = new Map<string, any>();
  for (const l of lines) {
    const k = l.activity?.id || "none";
    const prev = at.get(k);
    if (!prev) {
      const copy = { ...l };
      at.set(k, copy);
      out.push(copy);
      continue;
    }
    prev.minutes = (prev.minutes || 0) + (l.minutes || 0);
    prev.overtimeMinutes = (prev.overtimeMinutes || 0) + (l.overtimeMinutes || 0);
    prev.amount = prev.amount != null && l.amount != null ? (Number(prev.amount) + Number(l.amount)).toFixed(2) : null;
  }
  return out;
};

/** A switch is saved as an Out and an In at the same minute; show it as one line. */
export const timeline = (punches: any[] = []): { punch: any; switched: boolean }[] =>
  punches
    .map((p, i) => ({ p, prev: punches[i - 1] }))
    .filter(({ p }) => !(p.type === "OUT" && p.reason === "SWITCH"))
    .map(({ p, prev }) => ({
      punch: p,
      switched: p.type === "IN" && !!prev && prev.type === "OUT" && prev.reason === "SWITCH" && prev.time === p.time,
    }));
